import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { useTranslate } from '@tolgee/react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import useToggleOnlineOrderTag from '@client/page/management/online-order/detail/logic/use-toggle-online-order-tag.ts';
import { ONLINE_ORDER_TAG_DOT_COLOR, ONLINE_ORDER_TAG_LIST } from './online-order-tags';

interface Props {
  orderID: string;
  tags: readonly string[];
}

// History-page kebab (⋮) menu: one Mark/Unmark entry per catalog tag.
// Lives in a row that is itself clickable, so every handler stops propagation.
function OnlineOrderTagMenu(props: Props) {
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();
  const [toggleTag, { loading }] = useToggleOnlineOrderTag();

  const handleToggle = (tagKey: string) => {
    toggleTag({ variables: { id: props.orderID, tag: tagKey } }).catch((e: Error) => {
      showAlert({ status: 'error', title: t('common.error', 'Error'), message: e.message });
    });
  };

  return (
    <Menu>
      <MenuButton
        disabled={loading}
        onClick={(e) => {
          e.stopPropagation();
        }}
        aria-label={t('online-order.tags.menu', 'Tags')}
        className="rounded-full p-1 text-gray-500 transition-colors hover:bg-gray-100 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </MenuButton>
      <MenuItems
        transition
        anchor="bottom end"
        className="z-10 rounded-xl border border-gray-200 bg-white p-1 shadow-md [--anchor-gap:4px] [--anchor-padding:8px]"
      >
        {ONLINE_ORDER_TAG_LIST.map((tag) => {
          const active = props.tags.includes(tag.key);
          return (
            <MenuItem key={tag.key}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle(tag.key);
                }}
                className="flex w-full items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm hover:bg-gray-100"
              >
                <span
                  className={classNames(
                    'h-2 w-2 rounded-full',
                    active ? ONLINE_ORDER_TAG_DOT_COLOR[tag.color] : 'bg-gray-300',
                  )}
                />
                {active
                  ? t('online-order.tags.unmark', 'Unmark {name}', { name: tag.label })
                  : t('online-order.tags.mark', 'Mark {name}', { name: tag.label })}
              </button>
            </MenuItem>
          );
        })}
      </MenuItems>
    </Menu>
  );
}

export default OnlineOrderTagMenu;
